// FEAT-050: SMARTDent read thunk 의 (managerGroupId, isSubEntity, receivedRecord) 해석 SSoT.
// 각 thunk 개별 재구현하던 mg 해석/sub-entity 판정 통합 (PR #498/#499 리뷰 근본원인).
//
// IMP-426: corpId 별 캐시. TTL 3초 = 한 번의 Refresh 클릭 내 병렬 호출(GetCorpAllocation/
// GetDealerAllocations/GetAllocationSubtree 등)만 합치고, 다음 클릭엔 항상 재조회한다 —
// receivedRecord 는 할당/회수 직후 바뀌는 실시간 예산 데이터라 managerGroupResolver.js(구조적
// 데이터, TTL 5분)와 같은 자릿수로 캐싱하면 Refresh 버튼을 눌러도 갱신 전 값이 보일 수 있다
// (IMP-426 사전 리뷰 Major). receivedRecord 오버라이드(IMP-390 dedup) 호출은 캐시를 우회한다
// — 호출자가 이미 조회해 둔 값을 그대로 쓰려는 의도라 캐시와 얽히면 IMP-390 의미가 흐려진다.
//
// PR #539 리뷰 반영 — managerGroupResolver.js 와 동일 구조: expiresAt 는 settle 시점에 찍고
// (in-flight 동안 null=항상 hit), evict 는 캐시 소유권을 확인한 뒤에만 삭제.
import { GetManagerGroupIdByCompanyId } from './managerGroupResolver';
import { resolveLicenseProduct } from './licenseProduct';
import { queryDealerAllocation, queryDealerAllocationByDealerId } from './smartDentQueries';
import { deriveMgrIdFromReceivedSk } from './smartDentQuota';

const CACHE_TTL_MS = 3000; // 같은 Refresh 클릭의 병렬 호출만 dedup — 다음 클릭까지 유지되지 않음

const cache = new Map(); // `${licenseAppId}:${corpId}` -> { promise, expiresAt: number|null }

const resolveScopeContextUncached = async (corpId, { receivedRecord, fresh, product } = {}) => {
    // fresh 가 아닐 때는 기존과 동일하게 단일 인자로 호출 — 기존 호출 시그니처 단언(테스트)
    // 을 그대로 보존하고, GetManagerGroupIdByCompanyId 쪽 fresh 기본값(false)에도 맡긴다.
    const ownMg = fresh
        ? await GetManagerGroupIdByCompanyId(corpId, { fresh: true })
        : await GetManagerGroupIdByCompanyId(corpId);
    if (ownMg) {
        // self-resolve(공유/부모 mg 포함): 자기 received(info:{ownMg}:{corpId}) 정확 조회.
        // allocatedBy 로 "상위가 할당한 received"(중간법인, ≠corpId)와 "자기 self-deal"(allocatedBy===corpId,
        // mg 소유자의 자기발급 → corp 원장 사용, sub-entity 아님)을 구분. exact 조회라 first-match 스캔 없음.
        // top-level(자기 received 없음)도 null → isSubEntity=false.
        const own = await queryDealerAllocation(null, ownMg, corpId, product);
        const isSub = !!own && own.allocatedBy !== corpId;
        return { managerGroupId: ownMg, isSubEntity: isSub, receivedRecord: isSub ? own : null };
    }
    // mg-null: 자기 dealerId 로 받은 레코드 조회 후 상위 mg 도출 (BUG-180/182/184).
    // IMP-390: 호출자가 넘긴 receivedRecord(값 또는 in-flight promise)가 있으면 재조회 생략.
    // await 는 promise/값 모두 안전. 미전달(undefined)이면 기존대로 조회.
    // ⚠️ 트레이드오프(PR #504 리뷰 Minor 수용): 호출자가 GetMyDealerAllocation 의 promise 를 넘기면,
    //   그 thunk 는 조회 실패를 삼켜 null 을 resolve 하므로 "정상 레코드 없음"과 "일시 오류"가 구분되지
    //   않는다 → mg-null 조회 실패 시 receivedRecord=null 로 fail-closed(예산 0 표시)되며 관측성은 손실.
    //   저위험(mg-null + 일시 오류 한정)으로 판단해 현행 유지. 관측성 복원이 필요하면 호출자가
    //   throw 전파하는 조회를 넘기거나 null 시 자체 재조회 폴백을 추가할 것.
    // BUG-211: 이 무필터 전체스캔(queryDealerAllocationByDealerId)을 GetMyDealerAllocation 등
    // 다른 thunk 도 같은 corpId 로 독립 실행해 완전히 동일한 응답이 중복됐다(Network 실측).
    // dedupe:true 는 항상 켜고, fresh 만 write 경로({fresh:true} 호출)에서 캐시 조회를
    // 건너뛰게 한다(결과는 여전히 write-through 되어 다음 read 는 혜택 — managerGroupResolver.js
    // 와 동일 계약, R3 리뷰 반영).
    const received = receivedRecord !== undefined
        ? await receivedRecord
        : await queryDealerAllocationByDealerId(corpId, { dedupe: true, fresh: !!fresh, product });
    const derivedMg = deriveMgrIdFromReceivedSk(received?.sk, corpId);
    return { managerGroupId: derivedMg, isSubEntity: !!derivedMg, receivedRecord: received };
};

export const resolveScopeContext = async (corpId, options = {}) => {
    const product = resolveLicenseProduct(options?.product, { handler: 'scopeContext.resolveScopeContext' });
    if (!product || !corpId || typeof corpId !== 'string') {
        return { managerGroupId: null, isSubEntity: false, receivedRecord: null };
    }
    if (options.receivedRecord !== undefined) {
        return resolveScopeContextUncached(corpId, { ...options, product }); // IMP-390 dedup 경로 — 캐시 우회
    }
    // IMP-426 PR #539 리뷰 Major: write 경로(AllocateToDealer 등)가 이 판정으로 write 대상
    // 파티션(sk)을 결정하므로, mg 재배정 직후에도 최신값이 필요하면 캐시를 완전히 우회한다
    // (managerGroupResolver.js 의 { fresh: true } 를 그대로 전파). 결과는 캐시에 반영하지
    // 않는다 — write 는 항상 최신을 요구하므로 여기서까지 캐시를 채울 이유가 없다.
    if (options.fresh) {
        return resolveScopeContextUncached(corpId, { fresh: true, product });
    }
    const cacheKey = `${product.licenseAppId}:${corpId}`;
    const cached = cache.get(cacheKey);
    if (cached && (cached.expiresAt === null || cached.expiresAt > Date.now())) {
        return cached.promise;
    }
    const entry = { promise: null, expiresAt: null }; // expiresAt=null → in-flight, 항상 hit
    entry.promise = resolveScopeContextUncached(corpId, { product }).then(
        (result) => {
            entry.expiresAt = Date.now() + CACHE_TTL_MS;
            return result;
        },
        (error) => {
            // managerGroupResolver.js 와 동일 불변식 — 일시 오류는 캐시하지 않아 다음 호출이
            // 재시도. 소유권 확인 후 evict(TTL 만료 후 시작된 새 조회를 늦게 실패한 예전
            // 조회가 지우지 않도록). 현재 하위 조회는 ddb.js read fallback 에 의해 reject
            // 하지 않지만, 그 전제가 깨지는 회귀에도 corpId 하나가 TTL 동안 실패로 고정되지
            // 않게 방어. resolveScopeContext 는 원래도 reject 를 그대로 전파하는 계약이라
            // 여기서도 rethrow.
            if (cache.get(cacheKey) === entry) cache.delete(cacheKey);
            throw error;
        },
    );
    cache.set(cacheKey, entry);
    return entry.promise;
};

// 모듈 스코프 캐시 초기화 — 테스트 격리와 Logout(계정 전환 시 이전 계정의 스코프
// 데이터 잔존 방지) 양쪽에서 사용하는 공용 API.
export const clearScopeContextCache = () => cache.clear();
